import React, { useState } from "react";
import { X, Sparkle } from "@phosphor-icons/react";

/**
 * Versioned "What's new" card shown once per release on Home.
 *
 * - LS key includes the version string so bumping `version` re-shows it
 *   for every existing user on next launch.
 * - Single "Got it" CTA + X close — either dismisses for this version.
 * - Add a new banner per release by bumping `BUILD_VERSION` and updating
 *   `RELEASE_HIGHLIGHTS`. No other code changes.
 *
 * Keeping the copy here (and not in legalCopy) because release notes
 * are short-lived marketing-style content and don't need to live with
 * the legal-compliance strings.
 */
const BUILD_VERSION = "81";
const LS_KEY = `tiplingo_whatsnew_seen_v${BUILD_VERSION}`;

const RELEASE_HIGHLIGHTS = [
  {
    title: "Cleaner subscription paywall",
    body: "TipLingo Pro Monthly and Yearly pricing now matches the App Store exactly, with no extra promo copy.",
  },
  {
    title: "Account deletion in Settings",
    body: "Tap Settings → Delete account to remove your TipLingo account and saved app data at any time.",
  },
  {
    title: "iPhone-optimized",
    body: "We've focused this release on the iPhone experience. iPad and tablet support will return in a future update.",
  },
];

export default function WhatsNewBanner() {
  // Lazy initializer reads localStorage during the first render so we
  // don't need a setState-in-effect (which the React 19 lint forbids).
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) === null;
    } catch {
      return false;
    }
  });

  const dismiss = () => {
    try {
      localStorage.setItem(LS_KEY, new Date().toISOString());
    } catch {
      /* no-op */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="whats-new-banner"
      className="mt-5 bg-gradient-to-br from-navy to-[#1A3F70] text-white rounded-2xl shadow-deep p-4 relative animate-fade-up"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss what&apos;s new"
        data-testid="whats-new-close"
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center transition-colors"
      >
        <X size={14} weight="bold" color="#fff" />
      </button>

      <div className="flex items-center gap-2 mb-2 pr-8">
        <div className="w-8 h-8 rounded-full bg-orange flex items-center justify-center shrink-0">
          <Sparkle size={16} weight="fill" color="#fff" />
        </div>
        <div>
          <div className="font-jakarta font-extrabold text-base leading-tight">
            What&apos;s new
          </div>
          <div className="text-white/60 text-[11px] uppercase tracking-wider font-jakarta font-bold">
            In this release
          </div>
        </div>
      </div>

      <ul className="space-y-2.5 mb-4">
        {RELEASE_HIGHLIGHTS.map((item) => (
          <li
            key={item.title}
            data-testid={`whats-new-item-${item.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "")}`}
            className="flex items-start gap-2.5"
          >
            <span
              aria-hidden
              className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <div className="flex-1">
              <div className="font-jakarta font-bold text-[13px] leading-snug">
                {item.title}
              </div>
              <div className="text-white/75 text-[12px] leading-snug font-jakarta font-medium">
                {item.body}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={dismiss}
        data-testid="whats-new-dismiss"
        className="w-full bg-orange hover:bg-orange-hover text-white font-jakarta font-extrabold text-sm rounded-full py-2.5 active:scale-95 transition-transform"
      >
        Got it
      </button>
    </div>
  );
}
